import { getItem, setItem } from "utils";

import { LOCAL_STORAGE_KEYS } from "@config";

export type NotificationSignalDismissedMap = Record<string, boolean>;
export type NotificationSignalCreditExhaustedMap = Record<string, boolean>;

export function readNotificationSignalDismissedMap(): NotificationSignalDismissedMap {
  return (
    getItem<NotificationSignalDismissedMap>(
      LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_DISMISSED,
    ) ?? {}
  );
}

export function readNotificationSignalCreditExhaustedMap(): NotificationSignalCreditExhaustedMap {
  return (
    getItem<NotificationSignalCreditExhaustedMap>(
      LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_CREDIT_EXHAUSTED,
    ) ?? {}
  );
}

export function isNotificationSignalHeaderDismissedForOwner(
  ownerId: string,
): boolean {
  return !!readNotificationSignalDismissedMap()[ownerId];
}

/**
 * Persists Ask Ed dismissal per owner.
 * This only suppresses the Ask Ed variant and must not affect needActive.
 */
export function markNotificationSignalHeaderDismissedForOwner(
  ownerId: string,
): void {
  if (!ownerId) {
    return;
  }
  const next: NotificationSignalDismissedMap = {
    ...readNotificationSignalDismissedMap(),
    [ownerId]: true,
  };
  setItem(LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_DISMISSED, next);
}

/**
 * Indicates whether a dismissed Ask Ed can be re-armed:
 * owner has dismissal record and has gone through credit <= 0.
 */
export function shouldResetNotificationSignalHeaderDismissedForOwner(
  ownerId: string,
): boolean {
  if (!ownerId) {
    return false;
  }
  const dismissed = readNotificationSignalDismissedMap()[ownerId];
  const creditExhausted = readNotificationSignalCreditExhaustedMap()[ownerId];
  return !!dismissed && !!creditExhausted;
}

/**
 * Keeps dismissal state consistent with credit transitions.
 * - If dismissed and credit <= 0, mark owner as "credit exhausted".
 * - If dismissed and credit recovers to > 0 after exhaustion, clear dismissal.
 *
 * Returns true when local storage was mutated.
 */
export function syncNotificationSignalHeaderDismissedWithCredits(
  ownerId: string,
  credits: number,
): boolean {
  if (!ownerId) {
    return false;
  }

  const dismissedMap = readNotificationSignalDismissedMap();
  const creditExhaustedMap = readNotificationSignalCreditExhaustedMap();
  const dismissed = !!dismissedMap[ownerId];
  const creditExhausted = !!creditExhaustedMap[ownerId];

  if (credits <= 0) {
    if (!dismissed || creditExhausted) {
      return false;
    }
    setItem(LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_CREDIT_EXHAUSTED, {
      ...creditExhaustedMap,
      [ownerId]: true,
    });
    return true;
  }

  if (!dismissed || !creditExhausted) {
    return false;
  }

  const nextDismissedMap = { ...dismissedMap };
  const nextCreditExhaustedMap = { ...creditExhaustedMap };
  delete nextDismissedMap[ownerId];
  delete nextCreditExhaustedMap[ownerId];

  if (Object.keys(nextDismissedMap).length > 0) {
    setItem(
      LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_DISMISSED,
      nextDismissedMap,
    );
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_DISMISSED);
  }

  if (Object.keys(nextCreditExhaustedMap).length > 0) {
    setItem(
      LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_CREDIT_EXHAUSTED,
      nextCreditExhaustedMap,
    );
  } else {
    localStorage.removeItem(
      LOCAL_STORAGE_KEYS.NOTIFICATION_SIGNAL_HEADER_CREDIT_EXHAUSTED,
    );
  }

  return true;
}
